/**
 * Centralized navigation utilities for React Router 6
 *
 * This module provides navigation functions that work with React Router 6's
 * data router pattern.
 */

// Router reference - set by RouterProvider in RR6
let routerRef = null

/**
 * Set the router reference (used by RR6 RouterProvider)
 * @param {Object} router - The RR6 router instance
 */
export const setRouter = router => {
  routerRef = router
}

/**
 * Set the redux store reference (no-op, kept for compatibility)
 */
export const setStore = () => {
  // No-op: kept for backwards compatibility during transition
}

/**
 * Get the current router reference
 * @returns {Object|null} The router instance
 */
export const getRouter = () => routerRef

/**
 * Navigate to a new location (push)
 * @param {string|Object} to - Path string or location object
 * @param {Object} options - Navigation options
 * @param {Object} options.state - State to pass to the new location
 * @param {boolean} options.replace - Use replace instead of push
 */
export const navigate = (to, options = {}) => {
  if (routerRef) {
    const path = typeof to === 'string' ? to : to.pathname || '/'
    const state =
      options.state || (typeof to === 'object' ? to.state : undefined)

    // Handle query parameters from location object
    let fullPath = path
    if (typeof to === 'object' && to.search) fullPath = `${path}${to.search}`

    if (options.replace) routerRef.navigate(fullPath, { replace: true, state })
    else routerRef.navigate(fullPath, { state })
  } else {
    // Fallback to window.location for edge cases
    const path = typeof to === 'string' ? to : to.pathname || '/'
    if (options.replace) window.location.replace(path)
    else window.location.href = path
  }
}

/**
 * Navigate to a new location using replace (no history entry)
 * @param {string|Object} to - Path string or location object
 * @param {Object} options - Navigation options
 */
export const navigateReplace = (to, options = {}) => {
  navigate(to, { ...options, replace: true })
}

/**
 * Navigate back in history
 * @param {number} delta - Number of entries to go back (default: -1)
 */
export const navigateBack = (delta = -1) => {
  if (routerRef) routerRef.navigate(delta)
  else window.history.go(delta)
}

/**
 * Build a URL with query parameters
 * @param {string} pathname - The base path
 * @param {Object} query - Query parameters object
 * @returns {string} The full URL with query string
 */
export const buildUrl = (pathname, query = {}) => {
  const params = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) params.append(key, value)
  })
  const queryString = params.toString()
  return queryString ? `${pathname}?${queryString}` : pathname
}

/**
 * Parse query parameters from a search string
 * @param {string} search - The search string (e.g., "?foo=bar")
 * @returns {Object} Query parameters as an object
 */
export const parseQuery = search => {
  const params = new URLSearchParams(search)
  const query = {}
  params.forEach((value, key) => {
    query[key] = value
  })
  return query
}

export default {
  setRouter,
  setStore,
  getRouter,
  navigate,
  navigateReplace,
  navigateBack,
  buildUrl,
  parseQuery
}
